import axios from 'axios';
import toast from 'react-hot-toast';

// Use proxy in development (Vite proxies /api to backend), or direct URL if specified
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '/api' : 'http://localhost:5000/api');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter function - will be set by auth context
let tokenGetter = null;

// Set the token getter function (called by auth context)
export const setTokenGetter = (getter) => {
  tokenGetter = getter;
};

// Request interceptor - add auth token from Better Auth
api.interceptors.request.use(
  async (config) => {
    // Try to get token from Better Auth via the getter function
    if (tokenGetter) {
      try {
        const token = await tokenGetter();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Handle network errors (no response from server)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Network Error:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        }
      });
      const errorMessage = error.message || 'Network Error';
      return Promise.reject(new Error(errorMessage));
    }
    
    const message = error.response?.data?.message || error.response?.data?.error || 'An error occurred';
    
    // Log full error for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: message,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
    }
    
    if (error.response?.status === 401) {
      // Session expired - clear cached data and redirect to login
      const currentPath = window.location.pathname;
      // Public paths that don't require authentication
      // Note: /:studioname catch-all means any non-reserved path is potentially public
      const publicPathPrefixes = ['/login', '/register', '/forgot-password', '/check-in', '/s/', '/public/', '/accept-invite'];
      // Protected paths that require auth (if not in these, might be a public studio page)
      const protectedPathPrefixes = ['/dashboard', '/schedule', '/classes', '/community', '/subscription', '/settings', '/rings', '/complete-profile'];
      const isPublicPath = publicPathPrefixes.some(path => currentPath.startsWith(path));
      const isProtectedPath = protectedPathPrefixes.some(path => currentPath.startsWith(path));

      // Only redirect to login from protected paths (not public studio pages)
      if (isProtectedPath) {
        console.warn('401 Unauthorized - Session expired, redirecting to login');
        localStorage.removeItem('customerData');
        localStorage.removeItem('better_auth_token');
        toast.error('Session expired. Please login again.');
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action');
    } else if (error.response?.status >= 500) {
      toast.error(`Server error: ${message}`);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  registerCustomer: (data) => api.post('/profile/register-customer', data),
  login: (credentials) => api.post('/auth/sign-in/email', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  updateCurrentUser: (data) => api.put('/profile/update-user', data),
  updateEmail: (data) => api.post('/profile/update-email', data),
  deleteAccount: (confirmText) => api.delete('/profile/delete-account', { data: { confirmText } }),
};

// Videos API
export const videosAPI = {
  getVideos: (params) => api.get('/videos', { params }),
  getVideo: (id) => api.get(`/videos/${id}`),
  downloadVideo: (videoId, quality) => api.post('/videos/download', { videoId, quality }),
  getDownloadedVideos: (studioId) => api.get(`/videos/studio/${studioId}/downloads`),
  getCategories: () => api.get('/videos/categories'),
  syncVideo: (videoData) => api.post('/videos/sync', videoData),
  syncVideos: (videos) => api.post('/videos/sync/batch', { videos }),
  deleteLocalVideos: () => api.delete('/videos/local'),
  // Thumbnail endpoints (loaded separately for performance)
  getThumbnail: (id) => api.get(`/videos/${id}/thumbnail`),
  getThumbnails: (ids) => api.post('/videos/thumbnails', { ids }),
};

// Schedules API
export const schedulesAPI = {
  getSchedules: (studioId, params) => api.get(`/schedules/studio/${studioId}`, { params }),
  getPublicSchedules: (studioId, params) => api.get(`/schedules/public/${studioId}`, { params }),
  getSchedule: (id) => api.get(`/schedules/${id}`),
  createSchedule: (data) => api.post('/schedules', data),
  updateSchedule: (id, data) => api.put(`/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/schedules/${id}`),
  markAsPlayed: (id) => api.post(`/schedules/${id}/playback`),

  // Recurring Schedules API
  createRecurringSeries: (data) => api.post('/schedules/recurring', data),
  getSeries: (seriesId) => api.get(`/schedules/series/${seriesId}`),
  getSeriesInstances: (seriesId, params) => api.get(`/schedules/series/${seriesId}/instances`, { params }),
  updateSeries: (seriesId, data) => api.put(`/schedules/series/${seriesId}`, data),
  updateSeriesFromInstance: (instanceId, data) => api.put(`/schedules/${instanceId}/series`, data),
  deleteSeries: (seriesId) => api.delete(`/schedules/series/${seriesId}`),
  deleteSeriesFromInstance: (instanceId) => api.delete(`/schedules/${instanceId}/series`),
};

// Marketing API
export const marketingAPI = {
  getMaterials: (params) => api.get('/marketing', { params }),
  getMaterial: (id) => api.get(`/marketing/${id}`),
  getCalendar: () => api.get('/marketing/calendar'),
  startCampaign: (data) => api.post('/marketing/campaign/start', data),
  getCategories: () => api.get('/marketing/categories'),
};

// Subscription API
export const subscriptionAPI = {
  getSubscription: (studioId) => api.get(`/subscriptions/${studioId}`),
  getTiers: () => api.get('/subscriptions/tiers'),
  updateTier: (studioId, tier) => api.put(`/subscriptions/${studioId}/tier`, { tier }),
  cancelSubscription: (studioId, data) => api.post(`/subscriptions/${studioId}/cancel`, data),
  getPaymentMethods: (studioId) => api.get(`/subscriptions/${studioId}/payment-methods`),
  addPaymentMethod: (studioId, data) => api.post(`/subscriptions/${studioId}/payment-methods`, data),
  getInvoices: (studioId, params) => api.get(`/subscriptions/${studioId}/invoices`, { params }),
};

// CRM API
export const crmAPI = {
  getCustomers: (studioId, params) => api.get(`/crm/${studioId}/customers`, { params }),
  getCustomer: (id) => api.get(`/crm/customers/${id}`),
  createCustomer: (studioId, data) => api.post(`/crm/${studioId}/customers`, data),
  updateCustomer: (id, data) => api.put(`/crm/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/crm/customers/${id}`),
  getMonthlyRevenue: (studioId, params) => api.get(`/crm/${studioId}/revenue`, { params }),
};

// Bookings API
export const bookingsAPI = {
  createBooking: (data) => api.post('/bookings/create', data),
  cancelBooking: (data) => api.delete('/bookings/cancel', { data }),
  getMyBookings: (studentId) => api.get('/bookings/my-bookings', { params: { studentId } }),
  getClassBookings: (scheduleId) => api.get(`/bookings/class/${scheduleId}`),
  checkIn: (data) => api.post('/bookings/check-in', data),
  // Combined endpoint - fetches bookings + goal data in one call for better performance
  getDashboardData: () => api.get('/bookings/dashboard'),
};

// Goals API (Weekly Goal Ring System)
export const goalsAPI = {
  getGoalData: () => api.get('/profile/weekly-goal'),
  setWeeklyGoal: (weeklyGoal) => api.put('/profile/weekly-goal', { weeklyGoal }),
  completeRing: (weekOf, goal, attended) => api.post('/profile/complete-ring', { weekOf, goal, attended }),
  updateRing: (weekOf, attended) => api.post('/profile/update-ring', { weekOf, attended }),
};

// Customer Subscriptions API
export const customerSubscriptionsAPI = {
  getSubscription: () => api.get('/customer-subscriptions/me'),
  createCheckoutSession: (data) => api.post('/customer-subscriptions/checkout', data),
  cancelSubscription: () => api.post('/customer-subscriptions/cancel'),
};

export default api;
